import { format } from 'date-fns'
import { useState } from 'react'
import Markdown from 'react-markdown'

import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { DOBInput } from '@/components/ui/DateInput'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phoneInput'
import { SubmitButton } from '@/components/ui/submit-button'
import { Textarea } from '@/components/ui/textarea'
import { usePhoneField } from '../../hooks/usePhoneField'
import { blurIfDateFieldStale } from '../../lib/dateField'
import { formatDateForApi } from '../../lib/utils'
import { ChatActionWithValue } from '../../models/insuranceApplication'

interface ChatInputProps {
  service: string
  handleSubmit: (data: ChatActionWithValue | FormData) => void
  type: string
  readonly?: boolean
  initialValue?: string
  disclosure?: string
  placeholder?: string
  /** A few lines of free text: Enter sends, Shift+Enter starts a new line. */
  multiline?: boolean
}
export function ChatInput({
  handleSubmit,
  service,
  type,
  readonly,
  initialValue,
  disclosure,
  placeholder,
  multiline,
}: ChatInputProps & React.RefAttributes<HTMLInputElement>) {
  const [value, setValue] = useState(initialValue ?? '')
  const phoneField = usePhoneField({ required: true })

  function onSubmit() {
    if (type === 'tel' && !phoneField.check(value)) return
    if (type === 'dob') {
      handleSubmit({
        content: {
          component: 'P',
          content: format(new Date(value), 'PPP'),
        },
        value: formatDateForApi(value) || '',
        service,
      })
    } else {
      handleSubmit({
        content: {
          component: 'P',
          content: type === 'currency' ? `$${Number(value).toLocaleString('en-US')}` : value,
        },
        value,
        service,
      })
    }

    setValue('')
  }

  function renderInput() {
    if (type === 'currency') {
      return (
        <div className="w-80 max-w-full">
          <CurrencyInput
            value={value === '' ? null : Number(value)}
            onChange={(amount) => setValue(amount === null ? '' : String(amount))}
            placeholder={placeholder}
            disabled={readonly}
          />
        </div>
      )
    }
    if (type === 'tel') {
      return (
        <PhoneInput
          value={value}
          onChange={(v) => setValue(v)}
          disabled={readonly}
          error={!!phoneField.error}
          // Input is w-full; the chat wants it shrunk to the field and right-aligned.
          className="w-auto"
        />
      )
    }
    if (type === 'dob') {
      return (
        <DOBInput
          value={value ? new Date(value) : undefined}
          onChange={(v) => setValue(v?.toISOString() || '')}
          minAge={18}
          inputProps={{ disabled: readonly, name: 'dob' }}
        />
      )
    }
    if (multiline) {
      return (
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
            e.preventDefault()
            e.currentTarget.form?.requestSubmit()
          }}
          placeholder={placeholder}
          disabled={readonly}
          className="w-80 max-w-full"
        />
      )
    }
    return (
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        type={type}
        placeholder={placeholder}
        disabled={readonly}
        // The form shrinks to the field, whose default width cuts off longer placeholders.
        className="w-80 max-w-full"
      />
    )
  }

  return (
    // A form, so Enter in the field submits like the arrow button.
    <form
      className="max-w-m6 mt-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (
          type === 'dob' &&
          blurIfDateFieldStale(event.currentTarget, 'dob', value ? new Date(value) : undefined)
        )
          return
        if (!readonly && value) onSubmit()
      }}
    >
      <div className="flex items-center justify-end">{renderInput()}</div>
      {phoneField.error && (
        <div className="mt-1 flex justify-end">
          <FieldError message={phoneField.error} />
        </div>
      )}

      {disclosure && (
        <div className="text-muted-foreground mt-2 text-[10px] leading-snug [&_a]:underline">
          <Markdown components={{ p: ({ children }) => <>{children}</> }}>{disclosure}</Markdown>
        </div>
      )}

      <div className="mt-2 flex items-center justify-end">
        <SubmitButton disabled={readonly || !value} />
      </div>
    </form>
  )
}
